import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, LogOut, Menu, X, CalendarDays, Building2, Users, BarChart3, Settings, BookOpen, UserCog } from "lucide-react";
import { useState, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg";

export default function Navbar() {
  const { user, isAuthenticated, isAdmin, isMember, logout } = useAuth();
  const isManager = user?.role === "manager";
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => { logout(); navigate("/"); };
  const isActive = (path) => location.pathname === path;

  const navLink = (to, label) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-rig-orange ${isActive(to) ? 'text-rig-charcoal' : 'text-rig-charcoal/60'}`}
      onClick={() => setMobileOpen(false)}
    >
      {label}
    </Link>
  );

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm' : 'bg-white/60 backdrop-blur-lg'}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto flex h-16 items-center justify-between px-6">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img src={LOGO_URL} alt="The Rig" className="h-10" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLink("/", "Home")}
          {navLink("/spaces", "Spaces")}
          {navLink("/about", "About")}
          {navLink("/contact", "Contact")}
          {isAuthenticated && isMember && navLink("/dashboard", "My Bookings")}
          {isAuthenticated && isManager && navLink("/manager", "Manager")}
          {isAuthenticated && isAdmin && navLink("/admin", "Admin")}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-sm" data-testid="user-menu-trigger">
                  <div className="h-7 w-7 rounded-full bg-rig-teal text-white flex items-center justify-center text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="font-semibold text-rig-charcoal">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <UserCog className="mr-2 h-4 w-4" />Profile Settings
                </DropdownMenuItem>
                {isMember && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <CalendarDays className="mr-2 h-4 w-4" />My Bookings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/member/guide")} data-testid="menu-member-guide">
                      <BookOpen className="mr-2 h-4 w-4" />Guide
                    </DropdownMenuItem>
                  </>
                )}
                {isManager && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/manager")} data-testid="menu-manager-dashboard">
                      <Building2 className="mr-2 h-4 w-4" />Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/manager/guide")} data-testid="menu-manager-guide">
                      <BookOpen className="mr-2 h-4 w-4" />Guide
                    </DropdownMenuItem>
                  </>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/spaces")} data-testid="menu-admin-spaces">
                      <Building2 className="mr-2 h-4 w-4" />Manage Spaces
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/members")} data-testid="menu-admin-members">
                      <Users className="mr-2 h-4 w-4" />Members
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/reports")} data-testid="menu-admin-reports">
                      <BarChart3 className="mr-2 h-4 w-4" />Reports
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/cms")} data-testid="menu-admin-cms">
                      <Settings className="mr-2 h-4 w-4" />Site Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/managers")} data-testid="menu-admin-managers">
                      <Building2 className="mr-2 h-4 w-4" />Managers
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/guide")} data-testid="menu-admin-guide">
                      <BookOpen className="mr-2 h-4 w-4" />Guide
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-rig-charcoal/60 hover:text-rig-charcoal transition-colors" data-testid="nav-signin-link">
                Sign In
              </Link>
              <Button
                onClick={() => navigate("/spaces")}
                className="bg-rig-orange hover:bg-rig-orange/90 text-white rounded-full px-6 py-2 text-sm font-bold shadow-lg shadow-rig-orange/25 transition-all hover:shadow-xl hover:shadow-rig-orange/30 active:scale-95"
                data-testid="nav-book-now-btn"
              >
                Book Now
              </Button>
            </div>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white/95 backdrop-blur-xl px-6 py-4 flex flex-col gap-4">
          {navLink("/", "Home")}
          {navLink("/spaces", "Spaces")}
          {navLink("/about", "About")}
          {navLink("/contact", "Contact")}
          {isAuthenticated && isMember && navLink("/dashboard", "My Bookings")}
          {isAuthenticated && isManager && navLink("/manager", "Manager")}
          {isAuthenticated && isAdmin && navLink("/admin", "Admin")}
          <div className="pt-2 flex flex-col gap-2">
            {isAuthenticated ? (
              <>
              <Button variant="ghost" onClick={() => { navigate("/profile"); setMobileOpen(false); }} className="w-full justify-start" data-testid="mobile-profile">
                <UserCog className="mr-2 h-4 w-4" />Profile Settings
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="w-full justify-start" data-testid="mobile-logout">
                <LogOut className="mr-2 h-4 w-4" />Logout
              </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => { navigate("/spaces"); setMobileOpen(false); }}
                  className="w-full bg-rig-orange text-white hover:bg-rig-orange/90 rounded-full font-bold"
                  data-testid="mobile-book-btn"
                >
                  Book Now
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate("/login"); setMobileOpen(false); }}
                  className="w-full text-rig-charcoal/60"
                  data-testid="mobile-login-btn"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
